import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';

import * as I from 'interfaces';

import {
  gramsToKilos,
  kilosToGrams,
  offsetDate,
  localDateSender,
  dateReader,
  kilosToTons,
  tonsToKilos,
} from 'helpers/UnitsFormatHandler';

import {
  type Plan,
  type WriteCage,
  type Company,
  type InitialState,
  type CageResponse,
  type FishTypeResponse,
  HarvestDemandPutSpeciesEnum,
} from 'client';
import Alert from 'helpers/Alert';
import {type AxiosError} from 'axios';

import {type TFunction} from 'i18next';
import {DEFAULT_CAGE_MAX_COUNT, DEFAULT_CAGE_MAX_DENSITY, DEFAULT_CAGE_MAX_VOLUME} from 'appConstants';
import {type Dispatch, type MutableRefObject, type SetStateAction} from 'react';
import {mutateAllTransfers} from 'helpers/refreshHandler';
import {mutateAll, mutateAllByValue} from 'hooks/swr/swrHookFactory';

export const cageInitialState = ({
  plan,
  defaultFishType,
  defaultCompany,
  selectedCage,
  selectedSiteId,
}: {
  plan: Plan;
  defaultFishType: FishTypeResponse;
  defaultCompany: Company;
  selectedCage?: CageResponse;
  selectedSiteId?: string;
}): I.ICage => {
  const initialCount = selectedCage?.initial?.count ? parseInt(selectedCage?.initial?.count.toString()) : 0;

  const offsetedDate = offsetDate(dateReader(plan.startDate), -1);
  const inputDate = selectedCage?.initial?.inputDate ? dateReader(selectedCage?.initial?.inputDate) : offsetedDate;
  const initialWeight = selectedCage?.initial?.weight ? kilosToGrams(selectedCage?.initial?.weight) : 0;
  const accumulatedBiomassHarvested = selectedCage?.initial?.accumulatedBiomassHarvested
    ? kilosToTons(selectedCage?.initial?.accumulatedBiomassHarvested)
    : 0;
  const accumulatedBiomassLost = selectedCage?.initial?.accumulatedBiomassLost
    ? kilosToTons(selectedCage?.initial?.accumulatedBiomassLost)
    : 0;

  const volume = selectedCage?.volume ? selectedCage?.volume : DEFAULT_CAGE_MAX_VOLUME;

  const maxDensity = selectedCage?.maxDensity ? selectedCage?.maxDensity : DEFAULT_CAGE_MAX_DENSITY;
  const maxCount = selectedCage?.maxCount ? selectedCage?.maxCount : DEFAULT_CAGE_MAX_COUNT;

  if (!selectedCage) {
    return {
      siteId: selectedSiteId ?? '',
      name: '',
      companyId: defaultCompany.id,
      initialCount: 0,
      inputDate: offsetedDate,
      initialWeight: 0,
      volume,
      maxDensity,
      id: '',
      fishTypeId: defaultFishType.id,
      species: HarvestDemandPutSpeciesEnum.Salmon,
      certificates: [],
      fishGroupName: null,
      fishGroupInventoryId: null,
      maxCount,
      fishGroupPropertyIds: [],
      planId: plan.id,
      accumulatedBiomassHarvested: 0,
      accumulatedBiomassLost: 0,
    };
  }

  if (selectedCage?.initial) {
    return {
      siteId: selectedCage.siteId,
      name: selectedCage.name,
      companyId: selectedCage?.initial.companyId,
      initialCount,
      inputDate,
      initialWeight,
      volume,
      maxDensity,
      id: selectedCage.id,
      fishTypeId: selectedCage.initial.fishTypeId ?? '',
      species: selectedCage.initial.species ?? HarvestDemandPutSpeciesEnum.Salmon,
      certificates: selectedCage.initial.certificates ?? [],
      fishGroupName: selectedCage.initial.fishGroupName ?? null,
      fishGroupInventoryId: selectedCage.initial.fishGroupInventoryId ?? null,
      maxCount,
      fishGroupPropertyIds: selectedCage.initial.fishGroupPropertyIds ?? [],
      planId: plan.id,
      accumulatedBiomassHarvested,
      accumulatedBiomassLost,
    };
  }

  return {
    siteId: selectedCage.siteId,
    name: selectedCage.name,
    companyId: defaultCompany.id,
    initialCount,
    inputDate,
    initialWeight,
    volume,
    maxDensity,
    id: selectedCage.id,
    fishTypeId: defaultFishType.id,
    species: HarvestDemandPutSpeciesEnum.Salmon,
    certificates: [],
    fishGroupName: null,
    fishGroupInventoryId: null,
    maxCount,
    fishGroupPropertyIds: [],
    planId: plan.id,
    accumulatedBiomassHarvested,
    accumulatedBiomassLost,
  };
};

const cageToSendCreate = (cage: I.ICage): WriteCage => {
  const _cage: WriteCage = {
    siteId: cage.siteId,
    name: cage.name,
    volume: cage.volume,
    maxDensity: cage.maxDensity,
    maxCount: cage.maxCount,
    planId: cage.planId,
  };

  if (cage.initialCount > 0 && cage.initialWeight > 0 && cage.inputDate) {
    const initial: InitialState = {
      inputDate: localDateSender(cage.inputDate),
      count: parseInt(cage.initialCount.toString()),
      weight: gramsToKilos(cage.initialWeight),
      fishTypeId: cage.fishTypeId,
      companyId: cage.companyId,
      species: cage.species,
      certificates: cage.certificates,
      fishGroupName: cage.fishGroupName ?? null,
      fishGroupInventoryId: cage.fishGroupInventoryId ?? null,
      fishGroupPropertyIds: cage.fishGroupPropertyIds,
      accumulatedBiomassHarvested: tonsToKilos(cage.accumulatedBiomassHarvested),
      accumulatedBiomassLost: tonsToKilos(cage.accumulatedBiomassLost),
    };
    _cage.initial = initial;
  }

  return _cage;
};

interface IHandleCommunicationSuccess {
  setCage: Dispatch<SetStateAction<I.ICage | undefined>>;
  setCreateUpdateCage: Dispatch<SetStateAction<boolean>>;
  isUpdate: boolean;
  planId: string | null;
  cage: CageResponse;
  inputMenuState: I.InputMenuState | undefined;
}

export const handleCommunicationSuccess = ({
  setCage,
  setCreateUpdateCage,
  planId,
  cage,
  inputMenuState,
}: IHandleCommunicationSuccess) => {
  setCreateUpdateCage(false);
  setCage(undefined);
  inputMenuState?.onClose && inputMenuState.onClose();

  mutate([planId, I.SWRKeyEnum.GET_PLAN_TREE_NAVIGATION]);
  mutate([cage.siteId, I.SWRKeyEnum.GET_SITE_CAGES]);
  mutate([cage.id, I.SWRKeyEnum.GET_CAGE]);
  mutate([planId, I.SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
  void mutate([planId, I.SWRKeyEnum.GET_CAGES_GROWTH]);
  void mutateAllByValue(cage.id);
  void mutateAllTransfers();
  void mutateAll(I.SWRKeyEnum.GET_SITE_CAGE_FORECAST);
  void mutateAll(I.SWRKeyEnum.GET_CAGE_QUALITY_GRADING_MODELS_OVERVIEW_TABLE);
};

const cageValidator = (cage: I.ICage, setCreateCage: Dispatch<SetStateAction<boolean>>, t: TFunction) => {
  if (cage.name.length === 0) {
    Alert.warning(t('cageNameError'));
    setCreateCage(false);
  } else if (!cage.inputDate) {
    Alert.warning(t('noDate'));
  } else {
    return cage;
  }
};

const backendOperator = async (operationAddEdit, cage: I.ICage, t: TFunction) => {
  const _cage = cage && cageToSendCreate(cage);
  if (operationAddEdit === I.methodType.edit) {
    if (cage.id) {
      return await backendCommunicator.updateCage(cage.id, _cage);
    }
    Alert.warning(t('noCageError'));
  } else if (operationAddEdit === I.methodType.add) {
    return await backendCommunicator.createCage(_cage);
  }
};

const addEditCageBackendHandler = async ({
  operationAddEdit,
  cage,
  createUpdateCage,
  setCreateUpdateCage,
  inputMenuDispatch,
  communicationSuccess,
  isMounted,
  t,
}: IHandleCommunicationCage) => {
  const _cage = createUpdateCage && cageValidator(cage, setCreateUpdateCage, t);

  const backendOperation = _cage && backendOperator(operationAddEdit, _cage, t);
  const response =
    backendOperation &&
    backendOperation
      .then((response) => {
        if (response && isMounted.current) {
          communicationSuccess(response.data);
          inputMenuDispatch && inputMenuDispatch({type: I.inputMenuActionType.closeMenu});
        }
        return response;
      })
      .catch((error: AxiosError) => {
        if (isMounted.current) {
          Alert.error(error);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setCreateUpdateCage(false);
        }
      });

  return await response;
};

export default addEditCageBackendHandler;

interface IHandleCommunicationCage {
  cage: I.ICage;
  createUpdateCage: boolean;
  operationAddEdit: I.methodType;
  setCreateUpdateCage: Dispatch<SetStateAction<boolean>>;
  inputMenuDispatch?: Dispatch<I.InputMenuAction>;
  communicationSuccess: (cage: CageResponse) => void;
  isMounted: MutableRefObject<boolean>;
  t: TFunction;
}
